// CustomNode.jsx
import React, { useMemo, useState } from 'react';
import { Handle, Position } from 'reactflow';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const StatPill = ({ label, value, tone = 'gray' }) => {
  const toneClasses =
    tone === 'blue'
      ? 'bg-blue-50 text-blue-700 border-blue-100'
      : tone === 'green'
      ? 'bg-green-50 text-green-700 border-green-100'
      : tone === 'amber'
      ? 'bg-amber-50 text-amber-700 border-amber-100'
      : tone === 'red'
      ? 'bg-red-50 text-red-700 border-red-100'
      : 'bg-gray-50 text-gray-700 border-gray-100';

  return (
    <div className={`px-2 py-1 rounded-md border text-[11px] leading-none ${toneClasses}`}>
      <span className="font-medium">{label}:</span> {value ?? 0}
    </div>
  );
};

const CustomNode = ({ id, data }) => {
  const [hovered, setHovered] = useState(false);

  const label = data?.label || '';
  const subLabel = data?.subLabel || '';

  const isEndNode = !!data?.isEndNode || label === 'End flow';
  const isBranchNode = !!data?.isBranchNode;
  const branches = data?.branches || [];

  const taskCounts = data?.taskCounts || {};
  const workflowTotals = data?.workflowTotals || null;

  const showDetail = hovered;

  const primaryRow = useMemo(() => {
    // Always show these 4 for compact “active stats”
    return [
      { k: 'waiting', label: 'Waiting', tone: 'gray' },
      { k: 'pending', label: 'Pending', tone: 'amber' },
      { k: 'processing', label: 'Processing', tone: 'blue' },
      { k: 'completed', label: 'Done', tone: 'green' },
    ];
  }, []);

  const extraRow = useMemo(() => {
    // Show if present
    const extras = [];
    if ((taskCounts?.failed ?? 0) > 0) extras.push({ k: 'failed', label: 'Failed', tone: 'red' });
    if ((taskCounts?.canceled ?? 0) > 0) extras.push({ k: 'canceled', label: 'Canceled', tone: 'gray' });
    return extras;
  }, [taskCounts]);

  return (
    <div
      className="relative"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm px-3 py-2 min-w-[220px]">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${data?.badgeBg || 'bg-blue-100'}`}>
            <FontAwesomeIcon
              icon={data?.icon}
              className={`h-4 w-4 ${data?.iconClass || 'text-blue-600'}`}
            />
          </div>

          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-gray-900 truncate">{label}</div>
            {!!subLabel && <div className="text-xs text-gray-500 truncate">{subLabel}</div>}
          </div>

          {typeof data?.totalLeads === 'number' && (
            <div className="ml-2 px-2 py-1 rounded-md bg-gray-50 border border-gray-100 text-[11px] text-gray-700 whitespace-nowrap">
              Leads: <span className="font-semibold">{data.totalLeads}</span>
            </div>
          )}
        </div>

        {/* always-visible compact stats */}
        <div className="mt-2 flex flex-wrap gap-1.5">
          {primaryRow.map((s) => (
            <StatPill key={s.k} label={s.label} value={taskCounts?.[s.k] ?? 0} tone={s.tone} />
          ))}
          {extraRow.map((s) => (
            <StatPill key={s.k} label={s.label} value={taskCounts?.[s.k] ?? 0} tone={s.tone} />
          ))}
        </div>

        {/* optional “overall totals” on Start node */}
        {workflowTotals && (
          <div className="mt-2 text-[11px] text-gray-500">
            Total workflow: {workflowTotals.waiting ?? 0} waiting · {workflowTotals.pending ?? 0} pending ·{' '}
            {workflowTotals.processing ?? 0} processing · {workflowTotals.completed ?? 0} done
          </div>
        )}
      </div>

      {/* Hover detail (keeps your old idea, but styled + aligned) */}
      {showDetail && (
        <div className="absolute left-1/2 top-full mt-2 -translate-x-1/2 z-20 w-[260px] rounded-xl border border-gray-200 bg-white shadow-xl p-3">
          <div className="text-xs font-semibold text-gray-900 mb-2">Task details</div>
          <div className="grid grid-cols-2 gap-2">
            <StatPill label="Waiting" value={taskCounts?.waiting ?? 0} tone="gray" />
            <StatPill label="Pending" value={taskCounts?.pending ?? 0} tone="amber" />
            <StatPill label="Processing" value={taskCounts?.processing ?? 0} tone="blue" />
            <StatPill label="Completed" value={taskCounts?.completed ?? 0} tone="green" />
            <StatPill label="Failed" value={taskCounts?.failed ?? 0} tone="red" />
            <StatPill label="Canceled" value={taskCounts?.canceled ?? 0} tone="gray" />
          </div>
          <div className="mt-2 text-[11px] text-gray-500">
            Node: <span className="font-medium text-gray-700">{id}</span>
          </div>
        </div>
      )}

      {/* Incoming */}
      <Handle type="target" position={Position.Top} id="in" style={{ width: 10, height: 10 }} />

      {/* Outgoing: match TemplateBuilder rules */}
      {!isEndNode && !isBranchNode && (
        <Handle type="source" position={Position.Bottom} id="out" style={{ width: 10, height: 10 }} />
      )}

      {!isEndNode && isBranchNode && (
        <>
          <Handle
            type="source"
            position={Position.Bottom}
            id={branches?.[0]?.id || 'a'}
            style={{ width: 10, height: 10, left: '35%' }}
          />
          <Handle
            type="source"
            position={Position.Bottom}
            id={branches?.[1]?.id || 'b'}
            style={{ width: 10, height: 10, left: '65%' }}
          />
        </>
      )}
    </div>
  );
};

export default CustomNode;
